import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './DeleteAccount.css';

const DeleteAccount = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    reason: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    // Simulate API call
    try {
      // In production, replace this with actual API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setSubmitStatus('success');
      setFormData({ email: '', reason: '', message: '' });
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const reasons = [
    'Not using the app anymore',
    'Found a better alternative',
    'Privacy concerns',
    'Too expensive',
    'Technical issues',
    'Other'
  ];

  return (
    <div className="delete-account-page">
      <div className="container">
        <div className="delete-account-content">
          <div className="delete-account-header">
            <div className="warning-icon">⚠️</div>
            <h1>Delete Your Account</h1>
            <p className="warning-text">
              This action is permanent and cannot be undone. All your data, including properties, documents, and settings will be permanently deleted.
            </p>
          </div>

          <div className="delete-account-form-wrapper">
            <form onSubmit={handleSubmit} className="delete-account-form">
              <div className="form-group">
                <label htmlFor="email">
                  Email Address <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter your registered email"
                  required
                />
                <small className="form-hint">
                  We'll send a confirmation link to this email before processing your request
                </small>
              </div>

              <div className="form-group">
                <label htmlFor="reason">
                  Reason for Leaving <span className="required">*</span>
                </label>
                <select
                  id="reason"
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select a reason</option>
                  {reasons.map((reason, index) => (
                    <option key={index} value={reason}>{reason}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message">
                  Additional Comments (Optional)
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Tell us more about your decision. Your feedback helps us improve."
                  rows="5"
                />
              </div>

              <div className="data-deletion-info">
                <h3>What will be deleted:</h3>
                <ul>
                  <li>✓ All property records and documents</li>
                  <li>✓ GPS boundary data</li>
                  <li>✓ Uploaded images and files</li>
                  <li>✓ Payment history</li>
                  <li>✓ Account settings and preferences</li>
                  <li>✓ Subscription information</li>
                </ul>
              </div>

              <div className="form-actions">
                <button
                  type="button"
                  className="btn-cancel"
                  onClick={() => window.history.back()}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-delete"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Processing...' : 'Submit Deletion Request'}
                </button>
              </div>

              {submitStatus === 'success' && (
                <div className="alert alert-success">
                  <strong>Request Submitted!</strong> We've sent a confirmation email to {formData.email}. Please check your inbox and confirm to proceed with account deletion.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="alert alert-error">
                  <strong>Error!</strong> Something went wrong. Please try again or contact support.
                </div>
              )}
            </form>

            <div className="alternative-options">
              <h3>Before you go...</h3>
              <p>Consider these alternatives instead of deleting your account:</p>
              <div className="alternatives-grid">
                <div className="alternative-card">
                  <span className="alt-icon">⏸️</span>
                  <h4>Pause Subscription</h4>
                  <p>Take a break without losing your data</p>
                </div>
                <div className="alternative-card">
                  <span className="alt-icon">🔒</span>
                  <h4>Export Your Data</h4>
                  <p>Download all your information first</p>
                </div>
                <div className="alternative-card">
                  <span className="alt-icon">💬</span>
                  <h4>Contact Support</h4>
                  <p>We're here to help resolve any issues</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccount;
